import * as React from "react";
import { useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { PaywallButtons } from "../PaywallContent";
import { FeatureType, getPaywallContent, PaidTagV2 } from "../paywallUtils";

type Props = {
  featureType: FeatureType;
  trackingData?: EventData;
  contextSource?: string;
};

/**
 * This component is used in the components: StrategyPaywallModal & NoticesAnalysisContent. It has a custom
 * title, description & video for each paywall type, as well as the
 * accompanying buttons which link to the upgrade pages.
 * @returns
 */
export function StrategyPaywallContent({
  featureType,
  trackingData,
  contextSource,
}: Props): JSX.Element {
  const { title, description, planIcon, planText } = getPaywallContent(featureType);

  return (
    <ContentContainer>
      <Content>
        <PaidTagV2 />
        <Text h1>{title}</Text>
        <Text>{description}</Text>
        <PlanContent>
          {planIcon && <UIcon icon={planIcon} />}
          {planText}
        </PlanContent>
        <PaywallButtons
          featureType={featureType}
          trackingData={trackingData}
          plansButtonText="View pricing"
          primaryButtonText="Upgrade"
        />
      </Content>
      <StrategyPaywallVideo
        featureType={featureType}
        trackingData={trackingData}
        contextSource={contextSource}
      />
    </ContentContainer>
  );
}

const ContentContainer = styled(Flex)({
  flexDirection: "row",
  padding: "24px",
  gap: "20px",
  "@media (max-width: 960px)": {
    flexDirection: "column-reverse",
  },
});

const Content = styled(Flex)({
  width: "300px",
  flexShrink: 0,
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: "24px",
  "@media (max-width: 960px)": {
    width: "100%",
  },
});

const PlanContent = styled(Flex)({
  gap: "8px",
  alignItems: "center",
});

function StrategyPaywallVideo({
  featureType,
  trackingData,
  contextSource,
}: {
  featureType: FeatureType;
  trackingData?: EventData;
  contextSource?: string;
}) {
  const { logEvent } = useTracking();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { videoUrl } = getPaywallContent(featureType);

  //  Track if already played - we only want to track video viewed once
  const [hasPlayed, setHasPlayed] = useState(false);

  const onPlayVideo = () => {
    if (!hasPlayed && (trackingData || contextSource)) {
      logEvent(EventNames.videoViewed, {
        "Context source": contextSource,
        ...trackingData,
      });
      setHasPlayed(true); // Set it has played after tracking
    }
    void videoRef.current?.play();
  };

  return (
    <VideoContainer>
      <Video
        autoFocus
        controls
        controlsList="nodownload"
        onPlayCapture={onPlayVideo}
        ref={videoRef}
      >
        <source src={videoUrl} type="video/mp4" />
      </Video>
    </VideoContainer>
  );
}

const VideoContainer = styled.div({
  flex: 1,
  position: "relative",
  aspectRatio: "16 / 9",
});

const Video = styled.video({
  borderRadius: "8px",
  width: "100%",
  height: "100%",
  objectFit: "contain",
  cursor: "pointer",
  "&:focus-visible": {
    outline: "none",
  },
});
